import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { InputAdornment, TextField, Select, MenuItem, Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { lightTheme } from '@gojitsu/web-ui-core';
import { Search as SearchIcon } from '@mui/icons-material';
import { useRecoilState } from 'recoil';
import { healthBenefitState } from '../../atoms/HealthBenefitState';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const useStyles = makeStyles()((theme) => ({
  searchForm: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
  },
  searchInput: {
    flexGrow: 2,
    backgroundColor: '#fff',
    '&>.MuiInputBase-root': {
      fontSize: 14,
      marginLeft: 20,
      height: 47,
      '&>.MuiOutlinedInput-input': {
        paddingTop: '15px',
        paddingBottom: '15px',
      },
    },
  },

  searchBtn: {
    width: 200,
    marginLeft: 20,
    height: 56,
    borderRadius: 3,
    backgroundColor: lightTheme.palette.secondary.main,
    color: 'rgb(255, 255, 255)',
    fontSize: '15px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    outline: 'none',
  },
  closeBtn: {
    padding: 8,
  },
  select: {
    width: 180,
  },
}));

const STATUS_SELECT = [
  { value: '', label: '--- Status ---' },
  { value: 'CREATED', label: 'CREATED' },
  { value: 'APPROVED', label: 'APPROVED' },
  { value: 'REJECTED', label: 'REJECTED' },
];

const IS_DSP_DRIVER_SELECT = [
  { value: '', label: '--- DSP driver ---' },
  { value: true, label: 'Is DSP driver' },
  { value: false, label: 'Is not DSP driver' },
];

const ENABLE_DISABLE = [
  { value: '', label: '--- Enabled ---' },
  { value: true, label: 'ENABLED' },
  { value: false, label: 'DISABLED' },
];

const ACTIVATED_STATUS = [
  { value: '', label: '--- Activated ---' },
  { value: true, label: 'ACTIVATED' },
  { value: false, label: 'INACTIVATED' },
];

export const genYearQuarterList = () => {
  const fromDate = moment('2021-01-01 00:00:00', 'YYYY-MM-DD HH:mm:ss');
  const toDate = moment();

  const options = [];

  let index = 0;
  while (toDate.isSameOrAfter(fromDate)) {
    const year = toDate.get('year');
    const quarter = toDate.get('quarter');

    options.push({
      value: index,
      label: `${year} - Quarter ${quarter}`,
      quarter: quarter,
      year: year,
    });
    index++;
    toDate.subtract(3, 'month');
  }

  return options;
};

// Generate year options from current year to 2021 (descending order)
const genYearOptions = () => {
  const currentYear = moment().year();
  const years = [];
  for (let year = currentYear; year >= 2021; year--) {
    years.push(year);
  }
  return years;
};

const YEAR_OPTIONS = genYearOptions();
const QUARTER_OPTIONS = [4, 3, 2, 1];

const CURRENT_YEAR = moment().year();
const CURRENT_QUARTER = moment().quarter();

// Calculate previous quarter
const previousQuarterMoment = moment().subtract(1, 'quarter');
const PREVIOUS_QUARTER = previousQuarterMoment.quarter();
const PREVIOUS_QUARTER_YEAR = previousQuarterMoment.year();

const QUARTER_OPTION = genYearQuarterList();
const DEFAULT_QUARTER_INDEX = 1;

const SearchForm = ({ getList, initValue, placeholder }) => {
  const [healthBenefits, setHealthBenefits] = useRecoilState(healthBenefitState);
  const { classes } = useStyles();
  const [keyword, setKeyword] = useState(initValue);

  const handleSearch = (e) => {
    e.preventDefault();
    setHealthBenefits({ ...healthBenefits, search_text: keyword, page: 1 });
    return false;
  };

  const handleChangeSearch = (event) => {
    setKeyword(event.target.value);
  };

  const handleResetSearch = (e) => {
    setKeyword('');
    setHealthBenefits({ ...healthBenefits, search_text: null });
    return false;
  };

  const handleChangeSelect = (event) => {
    setHealthBenefits({
      ...healthBenefits,
      status: event.target.value,
      page: 1,
    });
  };

  const handleChangeYear = (event) => {
    const value = event.target.value;

    setHealthBenefits((previous) => ({
      ...previous,
      years: value ? [value] : [],
      page: 1,
    }));
  };

  const handleChangeQuarter = (event) => {
    const value = event.target.value;

    setHealthBenefits((previous) => ({
      ...previous,
      quarters: value ? [value] : [],
      page: 1,
    }));
  };

  const handleCloseYearSelect = () => {
    // If no year is selected, set to current year
    if (!healthBenefits.years || healthBenefits.years.length === 0) {
      setHealthBenefits((previous) => ({
        ...previous,
        years: [CURRENT_YEAR],
        page: 1,
      }));
    }
  };

  const handleCloseQuarterSelect = () => {
    // If no quarter is selected, set to previous quarter
    if (!healthBenefits.quarters || healthBenefits.quarters.length === 0) {
      setHealthBenefits((previous) => ({
        ...previous,
        quarters: [PREVIOUS_QUARTER],
        page: 1,
      }));
    }
  };

  const handleChangeEnable = (event) => {
    setHealthBenefits({
      ...healthBenefits,
      enabled: event.target.value,
      page: 1,
    });
  };

  const handleChangeActivated = (event) => {
    setHealthBenefits({
      ...healthBenefits,
      activated: event.target.value,
      page: 1,
    });
  };

  const handleChangeIsDSPDriver = (event) => {
    setHealthBenefits({
      ...healthBenefits,
      is_dsp_driver: event.target.value,
      page: 1,
    });
  };

  useEffect(() => {
    // Reset all filters to default values when component mounts
    setHealthBenefits((previous) => ({
      ...previous,
      years: [PREVIOUS_QUARTER_YEAR],
      quarters: [PREVIOUS_QUARTER],
      status: '',
      enabled: '',
      activated: '',
      is_dsp_driver: '',
      search_text: '',
      page: 1,
    }));
    // Also reset the local keyword state
    setKeyword('');
  }, []);

  const isLoading = healthBenefits.isListing;

  return (
    <form className={classes.searchForm} noValidate autoComplete="off" onSubmit={handleSearch}>
      <Select variant="outlined" displayEmpty value={healthBenefits.status} onChange={handleChangeSelect} className={classes.select} style={{ height: '47px' }} disabled={isLoading}>
        {STATUS_SELECT.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <Select
        variant="outlined"
        displayEmpty
        value={typeof healthBenefits.enabled !== 'undefined' ? healthBenefits.enabled : ''}
        onChange={handleChangeEnable}
        className={classes.select}
        style={{ height: '47px', marginLeft: '20px' }}
        disabled={isLoading}
      >
        {ENABLE_DISABLE.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <Select
        variant="outlined"
        displayEmpty
        value={typeof healthBenefits.activated !== 'undefined' ? healthBenefits.activated : ''}
        onChange={handleChangeActivated}
        className={classes.select}
        style={{ height: '47px', marginLeft: '20px' }}
        disabled={isLoading}
      >
        {ACTIVATED_STATUS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <Select
        variant="outlined"
        displayEmpty
        value={typeof healthBenefits.is_dsp_driver !== 'undefined' ? healthBenefits.is_dsp_driver : ''}
        onChange={handleChangeIsDSPDriver}
        className={classes.select}
        style={{ height: '47px', marginLeft: '20px' }}
        disabled={isLoading}
      >
        {IS_DSP_DRIVER_SELECT.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <Select
        variant="outlined"
        value={healthBenefits.years?.[0] || ''}
        onChange={handleChangeYear}
        onClose={handleCloseYearSelect}
        className={classes.select}
        style={{ height: '47px', marginLeft: '20px' }}
        displayEmpty
        disabled={isLoading}
      >
        <MenuItem value="" disabled>
          --- Year ---
        </MenuItem>
        {YEAR_OPTIONS.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
      <Select
        variant="outlined"
        value={healthBenefits.quarters?.[0] || ''}
        onChange={handleChangeQuarter}
        onClose={handleCloseQuarterSelect}
        className={classes.select}
        style={{ height: '47px', marginLeft: '20px' }}
        displayEmpty
        disabled={isLoading}
      >
        <MenuItem value="" disabled>
          --- Quarter ---
        </MenuItem>
        {QUARTER_OPTIONS.map((quarter) => (
          <MenuItem key={quarter} value={quarter}>
            Quarter {quarter}
          </MenuItem>
        ))}
      </Select>
      <TextField
        onChange={handleChangeSearch}
        value={keyword}
        variant="outlined"
        id="standard-start-adornment"
        className={classes.searchInput}
        placeholder={placeholder}
        disabled={isLoading}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment className={classes.searchIcon} position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: keyword && (
              <IconButton className={classes.closeBtn} onClick={() => handleResetSearch()} size="large" disabled={isLoading}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            ),
          },
        }}
      />
      <Button type="submit" variant="contained" style={{ height: '47px' }} className={classes.searchBtn} disabled={isLoading}>
        {isLoading ? 'Searching...' : 'Search'}
      </Button>
    </form>
  );
};

export default React.memo(SearchForm);
